import type { ComposerUploadFailure } from '@/components/org-chat/composer-upload-lifecycle';
import type {
  PersistedUpload,
  PersistedUploadPatch,
} from './persisted-upload-record';

/**
 * The background upload manager's contracts (#4844): the server half it
 * reports to, the store that keeps each file across a reload, and the jobs
 * it exposes to the UI.
 */

export type PendingUploadFailureReason =
  'too_large' | 'network' | 'server' | 'file_unavailable';

/** The server's word for a composer upload failure. */
export function pendingUploadFailureReason(
  failure: Omit<ComposerUploadFailure, 'filename'>
): PendingUploadFailureReason {
  if (failure.requiresReselect) return 'file_unavailable';
  if (failure.reason === 'too-large') return 'too_large';
  if (failure.reason === 'storage-full') return 'server';
  // The file itself could not be read or no longer exists: attach it again.
  if (failure.reason === 'file-missing' || failure.cause === 'unreadable')
    return 'file_unavailable';
  return failure.cause === 'network' || failure.cause === 'interrupted'
    ? 'network'
    : 'server';
}

/** The server half (#4851), injected so the manager runs without React. */
export type BackgroundUploadServer = {
  progress: (
    pendingSendId: string,
    updates: Array<{ uploadId: string; uploadedBytes: number }>
  ) => Promise<unknown>;
  complete: (args: {
    pendingSendId: string;
    uploadId: string;
    storageKey: string;
    workspaceFileId: string;
  }) => Promise<unknown>;
  fail: (args: {
    pendingSendId: string;
    uploadId: string;
    reason: PendingUploadFailureReason;
  }) => Promise<unknown>;
  remove: (args: {
    pendingSendId: string;
    uploadId: string;
  }) => Promise<unknown>;
  /** File states of a thread's waiting send, if it is still waiting and ours. */
  pendingFiles: (orgThreadId: string) => Promise<null | {
    pendingSendId: string;
    files: Array<{ uploadId: string; status: string }>;
  }>;
};

export type BackgroundUploadStore = {
  persistUpload: (
    record: Omit<PersistedUpload, 'blobStore' | 'createdAt' | 'updatedAt'>,
    file: Blob
  ) => Promise<PersistedUpload>;
  updateUpload: (
    uploadId: string,
    patch: PersistedUploadPatch
  ) => Promise<unknown>;
  listUploads: () => Promise<PersistedUpload[]>;
  readUploadFile: (record: PersistedUpload) => Promise<File | null>;
  deleteUpload: (uploadId: string) => Promise<unknown>;
  /** Whether the browser can keep a file right now; absent means it cannot. */
  probeDurableStorage?: () => Promise<boolean>;
};

export type BackgroundUploadJob = {
  uploadId: string;
  pendingSendId: string;
  orgThreadId: string;
  /** The task's route id (`ot_…`), when known: what its URL shows. */
  routeRef?: string;
  orgSlug: string;
  filename: string;
  sizeBytes: number;
  state: 'uploading' | 'failed';
  failure?: PendingUploadFailureReason;
  /** Every byte is in and stored; only the server's record is still owed. */
  saving?: boolean;
  /**
   * Its copy could not be kept in this browser: it uploads from this tab
   * only, and a reload loses it (the server's lease then ends the wait).
   */
  durable?: false;
};

/** The waiting send a handed-off upload belongs to. */
export type PendingUploadTarget = {
  pendingSendId: string;
  orgThreadId: string;
  /** The task's route id (`ot_…`): the pill hides on, and links to, it. */
  routeRef?: string;
};
